import { formatApiErrorDetail } from "./api";

/**
 * On-page failure messages for the redrawn Settings surface (issue #976),
 * extracted out of the component files that need them so #989 round 2's N3
 * can prove #988's two fixes BEHAVIOURALLY — call the real function with a
 * real input and read the real return value — rather than only by matching
 * the source string those component files also carry (`SettingsScreen.test.js`'s
 * own pins). Component files transitively import `components/ui/*`, which
 * imports `@/lib/utils` — an alias jest cannot resolve (this repo's own
 * stated reason it mounts no components in unit tests) — so a real `import`
 * of any of them would fail before a single assertion ran. This file has no
 * such dependency: plain JS, one import (`./api`, itself alias-free), safely
 * importable by a real test — `settingsErrorMessages.test.js` does exactly
 * that, the same shape `lib/signOut.test.js` already uses for
 * `lib/signOut.js`.
 *
 * `resolveOrFallback` (#989 round 3, O1 — adversarial review) — the ONE
 * guard every resolver below shares, after the review found the original
 * `resolveSaveErrorMessage` (and every sibling this round added) never
 * actually reached its own drawn fallback word: `formatApiErrorDetail`
 * NEVER returns a falsy value — `detail == null` alone already resolves to
 * ITS OWN truthy generic string ("Something went wrong. Please try
 * again."), so `formatApiErrorDetail(detail) || "Save failed"` could never
 * see "Save failed" from the outside, for ANY input — there is no `detail`
 * that makes `formatApiErrorDetail`'s return value falsy; only `detail`
 * being null to begin with was ever the real signal. The fix (first proven
 * for `resolveInviteErrorMessage`, #988 N1) is to test `detail` itself
 * BEFORE formatting it, not the formatted result: `(detail != null &&
 * formatApiErrorDetail(detail)) || fallback`. Every resolver below is a
 * one-line wrapper around this, differing only in which drawn word is the
 * fallback.
 */
function resolveOrFallback(detail, fallback) {
  return (detail != null && formatApiErrorDetail(detail)) || fallback;
}

/**
 * Issue #988, N1 — `CoachAccessCardNew.jsx`'s (and, since #989 round 3,
 * `AddPlayerCard.jsx`'s — same fallback word, same shape) invite-refused
 * fallback. Round 2 review, F9: `formatApiErrorDetail(null)` returns
 * "Something went wrong…", never `null` — so the fallback has to key off
 * the SERVER'S body (`detail == null`), not off `formatApiErrorDetail`'s
 * always-truthy return value. N1: `detail` can be a server body that IS
 * present but falsy once formatted — `""` or `[]` — in which case the OLD
 * ternary (`detail != null ? formatApiErrorDetail(detail) : "..."`)
 * produced a falsy `inviteError` and rendered no callout AND no toast at
 * all. The invite route always sends a string today, so this couldn't be
 * reached in practice, but the guard holds even if that ever changes.
 */
export function resolveInviteErrorMessage(detail) {
  return resolveOrFallback(detail, "Could not send invite");
}

/**
 * Issue #988, D57 — the former `AccountProfileCardNew` (#1135)'s name/mobile save
 * failures, and (since #976/#989) `HealthInfoCard.jsx`'s and
 * `CoachProfileCard.jsx`'s own profile save — all three drawn with the
 * identical word ("Save failed", `SettingsHealth.dc.html`'s/
 * `SettingsCoach.dc.html`'s own `save-failed` state). Each caller hands
 * the result to its own `set*Error` state setter, and NEITHER calls
 * `toast.error` — D57 says a failure stays on the page and a toast only
 * confirms. The shipped cards (`AccountProfileCard.jsx`, the bare
 * `HealthInfoCard`/`CoachProfileCard` calls with no `appearance`/`kitType`)
 * are unchanged; this function is used by the new/r5/kitType paths only.
 */
export function resolveSaveErrorMessage(e) {
  return resolveOrFallback(e?.response?.data?.detail, "Save failed");
}

/**
 * Issue #976/#989 — `PasskeysCard.jsx`'s add-passkey failure, the drawn
 * word (`SettingsPasskeys.dc.html`'s own `add-failed` state: "Could not
 * add a passkey on this device"). r5 path only.
 */
export function resolvePasskeyAddErrorMessage(e) {
  return resolveOrFallback(e?.response?.data?.detail, "Could not add a passkey on this device");
}

/**
 * Issue #976/#989 — `PasskeysCard.jsx`'s remove-passkey failure. Not its
 * own drawn STATE, but `SettingsPasskeys.dc.html`'s own commentary names
 * the word directly: "A failed remove says 'Remove failed' in the same
 * place." r5 path only.
 */
export function resolvePasskeyRemoveErrorMessage(e) {
  return resolveOrFallback(e?.response?.data?.detail, "Remove failed");
}

/**
 * Issue #976/#989 — `CoachProfileCard.jsx`'s photo upload failure. Not
 * drawn with its own word (no photo-failure state exists in
 * `SettingsCoach.dc.html`), but carries the identical bug shape as every
 * resolver above and is fixed for the same reason — the shipped fallback
 * text ("Photo upload failed") is what this function's own callers already
 * used; O1 is about the guard, not the word. kitType path only.
 *
 * THE ONE EXTRA TIER (unlike every other resolver here): the shipped chain
 * is `formatApiErrorDetail(detail) || e.message || "Photo upload failed"`
 * — `e.message` is a REAL middle fallback, from a client-side failure
 * (`resizePhotoToDataUrl` throwing, e.g. a canvas that can't encode the
 * chosen format), not a server response at all. Preserved here rather
 * than dropped: O1's fix is the `detail != null` guard on the FIRST tier,
 * not a reason to lose a genuinely useful client-side message on the
 * second.
 */
export function resolvePhotoUploadErrorMessage(e) {
  return resolveOrFallback(e?.response?.data?.detail, e?.message || "Photo upload failed");
}

/** The remove half of the pair above — same reasoning, "Couldn't remove
 * the photo". kitType path only. */
export function resolvePhotoRemoveErrorMessage(e) {
  return resolveOrFallback(e?.response?.data?.detail, "Couldn't remove the photo");
}
